import { z } from 'zod'
import { hcpAddressKindReadSchema } from '../enums/hcp-address-kind.enum.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { irrigationPartKindSchema } from '../enums/irrigation-part-kind.enum.js'

/**
 * A material category in Housecall Pro.
 *
 * HCP's own id rather than one of ours: categories are read from HCP and
 * never edited here, so its id is the only identity they have.
 */
export const hcpCategoryDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  parentUuid: z.string().nullable(),
})

export type HcpCategoryDto = z.infer<typeof hcpCategoryDtoSchema>

export const hcpCategoryListDtoSchema = z.object({
  categories: z.array(hcpCategoryDtoSchema),
})

export type HcpCategoryListDto = z.infer<typeof hcpCategoryListDtoSchema>

/** Which subcategory a part picker reads from. Null means every irrigation material. */
export const irrigationPartCategoryDtoSchema = z.object({
  kind: irrigationPartKindSchema,
  categoryUuid: z.string().nullable(),
})

/**
 * How this install reads Housecall Pro, and how fresh its copy is.
 *
 * `configured: false` is a supported state, not an error: a deployment
 * without a key gets a settings screen that says so rather than one that
 * fails to load.
 */
/**
 * A Housecall Pro service from the copy: labor, a drive charge, or anything
 * else the account sells. Money as an exact decimal string of dollars.
 */
export const hcpServiceDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  price: moneyAmountSchema,
  taxable: z.boolean(),
  /** HCP's category for the service, which is what tells drive charges apart. */
  categoryId: z.number().int().nullable(),
  categoryName: z.string().nullable(),
})

export type HcpServiceDto = z.infer<typeof hcpServiceDtoSchema>

export const hcpServiceListDtoSchema = z.object({
  services: z.array(hcpServiceDtoSchema),
})

export type HcpServiceListDto = z.infer<typeof hcpServiceListDtoSchema>

/** One town's irrigation drive charge, read from the chosen category. */
export const driveChargeDtoSchema = z.object({
  town: z.string(),
  service: hcpServiceDtoSchema,
})

/**
 * The switch in Settings that lets this app create things in HCP's live
 * account, and who changed it last.
 */
export const hcpWritesSwitchDtoSchema = z.object({
  switchedOn: z.boolean(),
  /** When it was last switched, and by whom. Null: never switched. */
  changedAt: z.string().datetime().nullable(),
  changedByName: z.string().nullable(),
  /**
   * Whether this deployment acts on the switch at all. Only production does,
   * so a development machine holding the live key never writes, whatever
   * its database says.
   */
  allowedHere: z.boolean(),
})

export type HcpWritesSwitchDto = z.infer<typeof hcpWritesSwitchDtoSchema>

export const housecallProStatusDtoSchema = z.object({
  configured: z.boolean(),
  /**
   * Whether this app may create things in HCP now: customers, addresses,
   * jobs. Needs the switch on and a production deployment (see
   * docs/irrigation.md, "The key is live"), so a screen offers "Create in
   * Housecall Pro" only where it would work.
   */
  writesEnabled: z.boolean(),
  writes: hcpWritesSwitchDtoSchema,
  /** The category irrigation materials are read from. Null until chosen. */
  irrigationCategory: hcpCategoryDtoSchema.nullable(),
  /** Every category under it, as of the last sync, for mapping pickers. */
  subcategories: z.array(hcpCategoryDtoSchema),
  /** One entry per picker, always all of them. */
  partCategories: z.array(irrigationPartCategoryDtoSchema),
  /** When materials were last read from HCP. Null: never. */
  syncedAt: z.string().datetime().nullable(),
  /** Materials in the copy, across the whole irrigation category. */
  materialCount: z.number().int().nonnegative(),
  /**
   * Materials the last sync could not read, by name.
   *
   * Named so somebody can find them in HCP and fix them. One bad row is
   * skipped rather than failing the sync, because a whole price book held
   * back by one typo is worse than a list missing that typo.
   */
  skippedMaterials: z.array(z.string()),

  /** When services were last read from HCP. Null: never. */
  servicesSyncedAt: z.string().datetime().nullable(),
  /** Services the last sync could not read, by name, for the same reason. */
  skippedServices: z.array(z.string()),
  /**
   * The labor service an irrigation repair is priced by, per client type.
   * Null until mapped, or when the mapped service has left HCP; a repair
   * cannot be priced without it.
   */
  residentialLaborService: hcpServiceDtoSchema.nullable(),
  commercialLaborService: hcpServiceDtoSchema.nullable(),
  /** The service category the irrigation drive charges are filed in. */
  driveChargeCategory: z
    .object({ id: z.number().int(), name: z.string() })
    .nullable(),
  /** The towns that category has a drive charge for, alphabetically. */
  driveCharges: z.array(driveChargeDtoSchema),
  /**
   * Towns with more than one drive charge in that category. The first is
   * used; listed so somebody can remove the extra in HCP.
   */
  duplicateDriveChargeTowns: z.array(z.string()),
  /**
   * The services a tree job's lines are made on: one line per tree, pruned
   * or removed. Null until chosen, or when the service has left HCP.
   */
  treePruningService: hcpServiceDtoSchema.nullable(),
  treeRemovalService: hcpServiceDtoSchema.nullable(),
})

export type HousecallProStatusDto = z.infer<typeof housecallProStatusDtoSchema>

/** A Housecall Pro material, offered in a part picker. */
export const irrigationPartDtoSchema = z.object({
  /** HCP's id for the material. */
  uuid: z.string(),
  name: z.string(),
  partNumber: z.string().nullable(),
  unitOfMeasure: z.string().nullable(),
  /** Exact decimal string of dollars, as HCP had it at the last sync. */
  price: moneyAmountSchema,
  taxable: z.boolean(),
})

export type IrrigationPartDto = z.infer<typeof irrigationPartDtoSchema>

export const irrigationPartListDtoSchema = z.object({
  parts: z.array(irrigationPartDtoSchema),
  /** When the copy was last read from HCP. Null: never. */
  syncedAt: z.string().datetime().nullable(),
  /**
   * Whether the list is narrowed to the picker's own subcategory.
   *
   * False when no subcategory is set for it yet, so the picker can say it is
   * showing every irrigation material rather than looking broken.
   */
  narrowed: z.boolean(),
})

export type IrrigationPartListDto = z.infer<typeof irrigationPartListDtoSchema>

/** An address on a Housecall Pro customer, offered for linking a site. */
export const hcpAddressDtoSchema = z.object({
  id: z.string(),
  /**
   * "service" or "billing" when HCP sent one of those, or HCP's own words
   * when it sent something else. Null when it does not say.
   */
  kind: hcpAddressKindReadSchema.nullable(),
  street: z.string().nullable(),
  streetLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  /** One line, as the link is shown: "4502 98th St, Lubbock". */
  label: z.string(),
})

export type HcpAddressDto = z.infer<typeof hcpAddressDtoSchema>

/** A Housecall Pro customer, offered for linking a client. */
export const hcpCustomerDtoSchema = z.object({
  id: z.string(),
  /** The company, else the person's name, else the email. */
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  addresses: z.array(hcpAddressDtoSchema),
  /** The client already linked to this customer, if any: one per customer. */
  linkedClient: z
    .object({ id: uuidSchema, name: z.string() })
    .nullable(),
})

export type HcpCustomerDto = z.infer<typeof hcpCustomerDtoSchema>

export const hcpCustomerListDtoSchema = z.object({
  customers: z.array(hcpCustomerDtoSchema),
  /** Every match HCP has, of which `customers` is the first page. */
  total: z.number().int().nonnegative(),
})

export type HcpCustomerListDto = z.infer<typeof hcpCustomerListDtoSchema>

